import { useState } from 'react';
import { OUTBOX_KIND_LABEL, failedOf, pendingOf, type OutboxEntry } from '@/domain';
import { useOutbox } from '@/application/container';
import {
  useOutboxEntries,
  type OutboxSyncDriver,
} from '@/application/outbox/useOutboxSync';
import { storedFailureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { useConfirmAction } from './useConfirmAction';
import { formatTime } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { Modal, modalStyles } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import styles from './NotSyncedTray.module.css';

/**
 * The not-synced tray. Section 9 rule 4.
 *
 * Two lists, because they need different things from the person reading them.
 * Pending is reassurance: the work is recorded and the phone will send it. A
 * failure is a decision: the server refused it, and somebody has to either try
 * again or accept that it is not going to land.
 *
 * Nothing is ever discarded on the technician's behalf. Discard exists, but a
 * person has to press it. It cannot be taken back, so the roles that are asked
 * before an action are asked before this one too: the request of 5 October
 * 2026 (third), section 2. Sync now only sends again what was already decided
 * and asks nothing.
 *
 * One refusal never reaches this tray when it arrives while the person is
 * looking: a start refused because the case is at Final Check and may not be
 * taken over. The screen shows that as a message with one OK button, and
 * `useStartWork` drops the entry, because OK is the person having seen it. The
 * request of 5 October 2026 (third), section 1. The same refusal coming back
 * from a start replayed later is listed here like any other.
 */
export function NotSyncedTray({
  driver,
  onClose,
}: {
  driver: OutboxSyncDriver | null;
  onClose: () => void;
}) {
  const outbox = useOutbox();
  const ask = useConfirmAction();
  const entries = useOutboxEntries();
  const [retrying, setRetrying] = useState(false);

  const pending = pendingOf(entries);
  const failed = failedOf(entries);

  async function retry() {
    if (driver === null) return;
    setRetrying(true);
    try {
      const result = await driver.run();
      toast(result.sent > 0 ? strings.sync.syncedNow : strings.sync.nothingSent);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Modal
      title={strings.sync.title}
      lead={failed.length > 0 ? strings.sync.leadFailed : strings.sync.leadPending}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.sync.close}</Button>
          <Button variant="primary" disabled={retrying || driver === null} onClick={() => void retry()}>
            {retrying ? strings.sync.retrying : strings.sync.retry}
          </Button>
        </>
      }
    >
      {entries.length === 0 && <p className={styles.clear}>{strings.sync.allClear}</p>}

      {failed.length > 0 && (
        <Section title={strings.sync.failed}>
          {failed.map((entry) => (
            <Row
              key={entry.id}
              entry={entry}
              onDiscard={() =>
                ask(
                  strings.confirm.discardQueued(OUTBOX_KIND_LABEL[entry.kind], entry.kitId),
                  () => outbox.discard(entry.id).then(() => toast(strings.sync.discarded)),
                  { title: strings.sync.title, confirmLabel: strings.sync.discard },
                )
              }
            />
          ))}
        </Section>
      )}

      {pending.length > 0 && (
        <Section title={strings.sync.pending}>
          {pending.map((entry) => (
            <Row key={entry.id} entry={entry} />
          ))}
        </Section>
      )}
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={modalStyles.field}>
      <span className={modalStyles.label}>{title}</span>
      <ul className={styles.list}>{children}</ul>
    </div>
  );
}

function Row({ entry, onDiscard }: { entry: OutboxEntry; onDiscard?: () => void }) {
  return (
    <li className={styles.row}>
      <div className={styles.what}>
        <b>{OUTBOX_KIND_LABEL[entry.kind]}</b>
        <span className={styles.kit}>{entry.kitId}</span>
      </div>
      {/* In words: what was kept may be a status, a mark, or what a browser called it. */}
      {entry.lastError !== null && <p className={styles.why}>{storedFailureWords(entry.lastError)}</p>}
      <div className={styles.meta}>
        <time dateTime={entry.createdAt}>{formatTime(entry.createdAt)}</time>
        {entry.attempts > 0 && (
          <span>
            {entry.attempts} {strings.sync.attempts}
          </span>
        )}
        {onDiscard !== undefined && (
          <button className={styles.discard} onClick={onDiscard}>
            {strings.sync.discard}
          </button>
        )}
      </div>
    </li>
  );
}
